import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';

import { AUTHENTICATION_MODAL_MODES, LEARNER_HELP_CENTER_LINK } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import AuthenticationV3CtaTracking from 'bundles/authentication/shared/AuthenticationV3CtaTracking';
import Separator from 'bundles/authentication/shared/Separator';
import SignupForm from 'bundles/authentication/shared/SignupForm';
import type { OnAuthenticationOptions } from 'bundles/authentication/shared/actions';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import SocialSSO from 'bundles/authentication/shared/components/authentication-modal/SocialSSO';
import type SSOError from 'bundles/authentication/shared/components/authentication-modal/SocialSSO/SSOError';
import type { EnterpriseSsoLoginInfo } from 'bundles/authentication/shared/hooks/useEnterpriseSsoCheck';
import type { ThirdPartyOrganizationsData } from 'bundles/authentication/shared/hooks/useUserThirdPartyOrganizationsQuery';
import type { ArkoseBotManagerContext, SuccessResponse } from 'bundles/authentication/shared/types/sharedTypes';
import { shouldShowLegalConsolidationAuthFlow, shouldShowTOSDisclaimer } from 'bundles/authentication/utils';
import { SSO_VARIANT, getSSOVariant } from 'bundles/authentication/utils/ssoUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import useSaveOnboardingOnSignup from 'bundles/onboarding-common/hooks/useSaveOnboardingOnSignup';
import SSOButton from 'bundles/user-account/components/SsoButton';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onSignup: (redirectURL: string, options?: OnAuthenticationOptions) => void;
  onSSOSuccess: (isRegistration: boolean, hasSavedOnboardingOnSignup?: boolean) => void;
  onSSOError: (error: SSOError) => void;
  title?: string;
  tagline?: string;
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  isEnterprise?: boolean;
  verificationEmailRedirectUrl?: string;
  shouldShowCustomSSO?: boolean;
  forceSSOLogin?: boolean;
  authProvider?: string;
  hideOrganizationSSO?: boolean;
  areAnyApiRequestsInFlight?: boolean;
  ssoError?: SSOError;
  resetSsoError?: () => void;
  enterpriseSsoEmailCheckInfo?: {
    enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
    onContinueWithSso: () => void;
    onEmailChange: (email: string) => void;
    resetEnterpriseSsoInfo: () => void;
  };
} & ThirdPartyOrganizationsData;

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);

const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '32px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '32px 16px',
    '@media (min-width: 600px)': {
      padding: '12px 16px',
    },
  },
  tagLine: {
    margin: '8px auto 0px',
    textAlign: 'center',
    width: '220px',
  },
  title: {
    color: 'var(--cds-color-neutral-primary)',
    fontFamily: 'OpenSans',
    fontSize: '1.5rem',
    lineHeight: '2rem',
    margin: '0',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '1.75rem',
      lineHeight: '2.25rem',
    },
  },
  navigation: {
    margin: '16px 0 0',
    textAlign: 'center',
  },
  link: {
    textDecoration: 'underline',
  },
  organization: {
    margin: '0',
    textAlign: 'center',
  },
  separator: {
    margin: '24px 0',
  },
  terms: {
    color: 'var(--cds-color-grey-600)',
    fontSize: '12px',
    lineHeight: '20px',
    margin: '16px 0 0',
    padding: '0 32px',
    textAlign: 'center',
    ':first-of-type': {
      margin: '0',
    },
    // workaround for nested selectors
    ':nth-of-type(n) a': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  },
});

const Signup = ({
  navigate,
  onSignup,
  onSSOSuccess,
  onSSOError,
  className,
  title,
  tagline,
  arkoseBotManagerContext,
  isEnterprise,
  verificationEmailRedirectUrl,
  shouldShowCustomSSO,
  forceSSOLogin,
  organizations,
  authProvider,
  hideOrganizationSSO,
  areAnyApiRequestsInFlight,
  ssoError,
  resetSsoError,
  enterpriseSsoEmailCheckInfo,
}: Props) => {
  const { saveOnboardingOnSignup } = useSaveOnboardingOnSignup();

  const navigateToLogin = () => {
    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  const navigateToOrganizationSSO = () => {
    navigate(AUTHENTICATION_MODAL_MODES.ORGANIZATIONSIGNUP, { referrer: AUTHENTICATION_MODAL_MODES.SIGNUP });
  };

  const handleSuccess = async (response: SuccessResponse) => {
    Instrumentation.register();

    const hasSavedOnboardingOnSignup = await saveOnboardingOnSignup();
    onSignup(response.redirectTo, {
      addShowTouAcceptParam: shouldShowLegalConsolidationAuthFlow(),
      hasSavedOnboardingOnSignup,
    });
  };

  const handleSSOSuccess = async (isRegistration: boolean) => {
    const hasSavedOnboardingOnSignup = await saveOnboardingOnSignup();
    onSSOSuccess(isRegistration, hasSavedOnboardingOnSignup);
  };

  const hideForm = organizations?.[0]?.supportsDegreeSSOLogin || forceSSOLogin;

  const ssoVariant = getSSOVariant({ authProvider, isEnterprise });

  return (
    <section {...css(className, styles.container)} data-catchpoint="authentication-box-content">
      {/* @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351 */}
      <header {...css(styles.header, styles.section)}>
        <h1 {...css(styles.title)}>{title ?? _t('Sign up')}</h1>
        <p {...css(styles.tagLine)}>
          {tagline ?? (
            <FormattedMessage message={_t('Learn on your own time from top \n universities and businesses.')} />
          )}
        </p>
      </header>
      {/* @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351 */}
      <div {...css(styles.section)}>
        {/* TODO: Consolidate the whitelist organization for SSO login logic to `getSSOVariant` utility */}
        {authProvider && (areAnyApiRequestsInFlight || shouldShowCustomSSO) && (
          <>
            <SSOButton provider={authProvider} organizations={organizations} loading={areAnyApiRequestsInFlight} />
            {!areAnyApiRequestsInFlight && !forceSSOLogin && <Separator {...css(styles.separator)} text={_t('or')} />}
          </>
        )}
        {!areAnyApiRequestsInFlight && !hideForm && (
          <SignupForm
            navigateToLogin={navigateToLogin}
            onSuccess={handleSuccess}
            verificationEmailRedirectUrl={verificationEmailRedirectUrl}
            arkoseBotManagerContext={arkoseBotManagerContext}
            enterpriseSsoProps={{
              ssoError,
              resetSsoError,
              isEnterpriseSsoLogin: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isEnterpriseSsoLogin,
              isLoading: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isLoading,
              isRedirecting: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isRedirecting,
              onEmailChange: enterpriseSsoEmailCheckInfo?.onEmailChange,
              onSubmit: enterpriseSsoEmailCheckInfo?.onContinueWithSso,
            }}
          />
        )}
        {ssoVariant === SSO_VARIANT.SOCIAL && (
          <>
            <Separator {...css(styles.separator)} text={_t('or')} />
            <SocialSSO onError={onSSOError} onSuccess={handleSSOSuccess} />
          </>
        )}
        <p {...css(styles.navigation, 'auth-form-type-navigation')}>
          <FormattedMessage
            message={_t('Already on Coursera? {loginLink}')}
            loginLink={
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'login' });
                    navigateToLogin();
                  };
                  return (
                    <TrackedLinkButton onClick={handleOnClick} trackingName="login" {...css(styles.link)}>
                      {_t('Log in')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            }
          />
        </p>
        {ssoVariant === SSO_VARIANT.SOCIAL && !hideOrganizationSSO && (
          <>
            {/* @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351 */}
            <Separator {...css(styles.separator)} />
            <p {...css(styles.organization)}>
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'signup_with_other_sso' });
                    navigateToOrganizationSSO();
                  };
                  return (
                    <TrackedLinkButton
                      onClick={handleOnClick}
                      trackingName="sign_up_with_organization_btn_click"
                      {...css(styles.link)}
                    >
                      {_t('Sign up with your organization')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            </p>
          </>
        )}
      </div>
      {/* @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351 */}
      <div {...css(styles.section, 'auth-form-tos-section')}>
        <p {...css(styles.terms)}>
          {shouldShowTOSDisclaimer() && (
            <FormattedMessage
              message={_t("I accept Coursera's {tosLink} and {privacyLink}. ")}
              privacyLink={
                <a href="/about/privacy" target="_blank">
                  {_t('Privacy Notice')}
                </a>
              }
              tosLink={
                <a href="/about/terms" target="_blank">
                  {_t('Terms of Use')}
                </a>
              }
            />
          )}
          <FormattedMessage
            message={_t('Having trouble logging in? {helpCenterLink}')}
            helpCenterLink={
              <a href={LEARNER_HELP_CENTER_LINK} target="_blank" rel="noopener noreferrer">
                {_t('Learner help center')}
              </a>
            }
          />
        </p>
        {!areAnyApiRequestsInFlight && !forceSSOLogin && (
          <p {...css(styles.terms)}>
            <FormattedMessage
              message={_t(
                'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
              )}
              privacyPolicyLink={
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  {_t('Privacy Policy')}
                </a>
              }
              tosLink={
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  {_t('Terms of Service')}
                </a>
              }
            />
          </p>
        )}
      </div>
    </section>
  );
};

export default Signup;
